/* sc-category-tree.js — Thin UI layer over hidden S&D checkboxes
 *
 * Architecture: Selections are scoped by parent gender section.
 * Level 1 desired key: "target_gender::Men"
 * Level 2 desired key: "main_category::Shoes::Men"  (scoped to gender)
 * Level 3 desired key: "sub_category::Sneakers::Men" (scoped to gender)
 *
 * Checkbox state = OR across all gender scopes for that filter value.
 * handleClick: toggles scoped desired, stamps all checkboxes, dispatches ONE change.
 * syncTreeState (MutationObserver): stamps desired onto replacement checkboxes,
 * NEVER dispatches events — preventing cascading AJAX cycles.
 */
(function () {
  var KEYS = ['target_gender', 'main_category', 'sub_category'];
  var container, hierarchy, lookup;
  var desired = {};   // scoped keys -> true/false
  var confirmed = {}; // scoped keys -> true (seen in URL at least once)
  var userCollapsed = {}; // scoped keys -> true when user manually collapsed an active parent

  function buildCheckboxLookup() {
    var r = { _valueToLabel: {} };
    KEYS.forEach(function (key) {
      r[key] = {};
      r._valueToLabel[key] = {};
      document.querySelectorAll(
        '#FacetFiltersFormMobile input.mobile-facets__checkbox[name*="' + key + '"]'
      ).forEach(function (cb) {
        var lbl = cb.closest('label') || cb.parentElement.querySelector('label');
        var m = lbl ? lbl.textContent.match(/\((\d+)\)/) : null;
        // Key by label text so GID-based S&D values match the hierarchy JSON
        var labelSpan = lbl && lbl.querySelector('.facet-checkbox__text');
        var labelText = labelSpan
          ? labelSpan.textContent.replace(/\s*\(\d+\)\s*$/, '').trim()
          : cb.value;
        r[key][labelText] = { el: cb, count: m ? parseInt(m[1], 10) : 0, active: cb.checked };
        if (cb.value !== labelText) r._valueToLabel[key][cb.value] = labelText;
      });
    });
    return r;
  }

  /* Build a scoped desired-map key */
  function skey(key, value, parentGender) {
    return key === 'target_gender'
      ? key + '::' + value
      : key + '::' + value + '::' + parentGender;
  }

  /* Convert scoped key to flat key for URL comparison: "main_category::Shoes::Men" -> "main_category::Shoes" */
  function flatKey(sk) {
    var p = sk.split('::');
    return p[0] + '::' + p[1];
  }

  function isDesired(key, value, parentGender) {
    return desired[skey(key, value, parentGender)] === true;
  }

  /* Is the checkbox for key=value needed? True if ANY gender scope is desired. */
  function isAnyDesired(key, value) {
    if (key === 'target_gender') return desired['target_gender::' + value] === true;
    var prefix = key + '::' + value + '::';
    for (var k in desired) {
      if (desired.hasOwnProperty(k) && desired[k] && k.indexOf(prefix) === 0) return true;
    }
    return false;
  }

  /* Does desired have ANY entry (even false) for this key::value? Prevents re-adoption of deselected items. */
  function hasEntry(key, value) {
    if (key === 'target_gender') return desired.hasOwnProperty('target_gender::' + value);
    var prefix = key + '::' + value + '::';
    for (var k in desired) {
      if (desired.hasOwnProperty(k) && k.indexOf(prefix) === 0) return true;
    }
    return false;
  }

  /* Check if a value exists under a gender in the hierarchy JSON */
  function hierHas(gender, key, value) {
    var g = hierarchy[gender];
    if (!g) return false;
    if (key === 'main_category') return g.hasOwnProperty(value);
    if (key === 'sub_category') {
      for (var c in g) {
        if (Array.isArray(g[c]) && g[c].indexOf(value) !== -1) return true;
      }
    }
    return false;
  }

  /* Read initial checkbox state into scoped desired + confirmed maps */
  function initDesiredFromCheckboxes(lkp) {
    var activeGenders = [];
    for (var g in lkp.target_gender) {
      if (!lkp.target_gender.hasOwnProperty(g)) continue;
      if (lkp.target_gender[g].active) {
        desired['target_gender::' + g] = true;
        confirmed['target_gender::' + g] = true;
        activeGenders.push(g);
      }
    }
    ['main_category', 'sub_category'].forEach(function (key) {
      for (var val in lkp[key]) {
        if (!lkp[key].hasOwnProperty(val) || !lkp[key][val].active) continue;
        activeGenders.forEach(function (g) {
          if (hierHas(g, key, val)) {
            var sk = skey(key, val, g);
            desired[sk] = true;
            confirmed[sk] = true;
          }
        });
      }
    });
  }

  /* Read URL to determine which tree-managed filters are active (flat keys).
   * Resolves GID-based S&D values back to label text via reverse map. */
  function getUrlFilterState() {
    var params = new URLSearchParams(window.location.search);
    var vtl = lookup._valueToLabel || {};
    var active = {};
    params.forEach(function (val, name) {
      KEYS.forEach(function (key) {
        if (name.indexOf(key) !== -1) {
          var resolved = (vtl[key] && vtl[key][val]) || val;
          active[key + '::' + resolved] = true;
        }
      });
    });
    return active;
  }

  /* Stamp all checkboxes based on desired state (OR across gender scopes).
   * Removes disabled attribute before checking so FormData includes tree-desired
   * values even when S&D marked them as 0-count. Safe because AJAX replaces
   * the DOM anyway, and syncTreeState re-stamps after each response. */
  function stampCheckboxes() {
    KEYS.forEach(function (key) {
      var group = lookup[key];
      for (var val in group) {
        if (!group.hasOwnProperty(val)) continue;
        var want = isAnyDesired(key, val);
        if (want) group[val].el.disabled = false;
        group[val].el.checked = want;
        group[val].active = want;
      }
    });
  }

  function nodeHtml(level, key, value, gender, info, isLeaf) {
    var dis = !info;
    var act = info && isDesired(key, value, gender);
    var cnt = info ? info.count : 0;
    var zero = cnt === 0 && !act && level !== 2;
    var cls = 'sc-tree__node sc-tree__node--l' + level +
      (dis ? ' sc-tree__node--disabled' : '') +
      (zero ? ' sc-tree__node--zero' : '') +
      (act ? ' sc-tree__node--active' + (isLeaf ? '' : ' sc-tree__node--expanded') : '');
    var attrs = 'data-level="' + level + '" data-filter-key="' + key +
      '" data-filter-value="' + value + '"' + (gender ? ' data-parent-gender="' + gender + '"' : '');
    var arrow = (dis || isLeaf) ? '' : '&#9654;';
    var countText = (level === 2 && cnt === 0) ? '' : '(' + cnt + ')';
    return '<div class="' + cls + '" ' + attrs + '>' +
      '<span class="sc-tree__label">' + value + '</span>' +
      '<span class="sc-tree__count">' + countText + '</span>' +
      '<span class="sc-tree__arrow">' + arrow + '</span></div>';
  }

  function renderTree(hier, lkp) {
    var html = '';
    for (var g in hier) {
      if (!hier.hasOwnProperty(g)) continue;
      var gi = lkp.target_gender[g];
      html += nodeHtml(1, 'target_gender', g, null, gi, false);
      html += '<div class="sc-tree__children' + (gi && isDesired('target_gender', g, null) ? ' sc-tree__children--visible' : '') +
        '" data-parent="' + g + '">';
      var cats = hier[g];
      for (var c in cats) {
        if (!cats.hasOwnProperty(c)) continue;
        var ci = lkp.main_category[c];
        html += nodeHtml(2, 'main_category', c, g, ci, false);
        var subs = cats[c];
        if (Array.isArray(subs) && subs.length) {
          html += '<div class="sc-tree__children' + (ci && isDesired('main_category', c, g) ? ' sc-tree__children--visible' : '') +
            '" data-parent="' + c + '">';
          subs.forEach(function (s) { html += nodeHtml(3, 'sub_category', s, g, lkp.sub_category[s], true); });
          html += '</div>';
        }
      }
      html += '</div>';
    }
    return html;
  }

  /* syncTreeState: called by MutationObserver after AJAX replaces .js-filter elements.
   * Rebuilds lookup, reconciles desired with URL state, stamps onto new checkboxes.
   * NEVER dispatches events — only handleClick dispatches change events. */
  function syncTreeState() {
    lookup = buildCheckboxLookup();
    var urlState = getUrlFilterState(); // flat keys: { "target_gender::Men": true, "main_category::Shoes": true }

    // 1. Update confirmed: scoped entries whose flat key is now in URL
    Object.keys(desired).forEach(function (sk) {
      if (desired[sk] && urlState[flatKey(sk)]) confirmed[sk] = true;
    });

    // 2. External removals: confirmed entries whose flat key vanished from URL
    Object.keys(desired).forEach(function (sk) {
      if (desired[sk] && confirmed[sk] && !urlState[flatKey(sk)]) {
        desired[sk] = false;
        delete confirmed[sk];
      }
    });

    // 3. Adopt externally-added filters (popstate, direct URL nav)
    Object.keys(urlState).forEach(function (fk) {
      var parts = fk.split('::');
      var key = parts[0], value = parts[1];
      if (key === 'target_gender') {
        if (!desired.hasOwnProperty('target_gender::' + value)) {
          desired['target_gender::' + value] = true;
          confirmed['target_gender::' + value] = true;
        }
      } else if (!hasEntry(key, value)) {
        // Truly new — assign to active genders that contain this value in hierarchy
        Object.keys(desired).forEach(function (gk) {
          if (gk.indexOf('target_gender::') !== 0 || !desired[gk]) return;
          var g = gk.split('::')[1];
          if (hierHas(g, key, value)) {
            var sk = skey(key, value, g);
            desired[sk] = true;
            confirmed[sk] = true;
          }
        });
      }
    });

    // 4. Clean up stale false entries whose flat key is no longer in URL
    Object.keys(desired).forEach(function (sk) {
      if (desired[sk] === false && !urlState[flatKey(sk)]) delete desired[sk];
    });

    // --- Stamp checkboxes (OR across genders) ---
    stampCheckboxes();

    // --- Update tree node visuals from scoped desired ---
    var nodes = container.querySelectorAll('.sc-tree__node');
    var anyActive = false;
    nodes.forEach(function (n) {
      var key = n.dataset.filterKey, value = n.dataset.filterValue;
      var pg = n.dataset.parentGender || null;
      var info = lookup[key] && lookup[key][value];
      var countEl = n.querySelector('.sc-tree__count');
      var wantActive = isDesired(key, value, pg);

      if (info) {
        var isL2 = n.dataset.level === '2';
        if (countEl) countEl.textContent = (isL2 && info.count === 0) ? '' : '(' + info.count + ')';
        n.classList.toggle('sc-tree__node--active', wantActive);
        n.classList.remove('sc-tree__node--disabled');
        // A row that is on screen never disappears when counts land (no
        // layout jump): a zero in the current context dims it instead. Rows
        // hidden since page load (no products at all) are revealed only if
        // they gain a count.
        var isZero = info.count === 0 && !wantActive && !isL2;
        if (!isZero) n.classList.remove('sc-tree__node--zero');
        n.classList.toggle('sc-tree__node--dim', isZero && !n.classList.contains('sc-tree__node--zero'));
        if (wantActive) anyActive = true;
      } else {
        n.classList.add('sc-tree__node--disabled');
        n.classList.remove('sc-tree__node--active', 'sc-tree__node--dim');
        if (n.dataset.level !== '2') n.classList.add('sc-tree__node--zero');
        if (countEl) countEl.textContent = '(0)';
        var sk = skey(key, value, pg);
        delete desired[sk];
        delete confirmed[sk];
      }
    });

    // Reveal counts now that they reflect filtered data
    container.querySelectorAll('.sc-tree__counts-pending').forEach(function (el) {
      el.classList.remove('sc-tree__counts-pending');
    });

    // --- Manage expand/collapse ---
    if (!anyActive) {
      container.querySelectorAll('.sc-tree__node--expanded').forEach(function (n) { n.classList.remove('sc-tree__node--expanded'); });
      container.querySelectorAll('.sc-tree__children--visible').forEach(function (c) { c.classList.remove('sc-tree__children--visible'); });
      userCollapsed = {}; // nothing active — reset collapse intent
    } else {
      nodes.forEach(function (n) {
        var sib = n.nextElementSibling;
        if (sib && sib.classList.contains('sc-tree__children') && n.dataset.level !== '3') {
          var pg = n.dataset.parentGender || null;
          var nsk = skey(n.dataset.filterKey, n.dataset.filterValue, pg);
          var wantActive = isDesired(n.dataset.filterKey, n.dataset.filterValue, pg);
          if (wantActive && !n.classList.contains('sc-tree__node--expanded') && !userCollapsed[nsk]) {
            n.classList.add('sc-tree__node--expanded');
            sib.classList.add('sc-tree__children--visible');
          }
          // Clean up stale collapse intent for nodes that are no longer active
          if (!wantActive && userCollapsed[nsk]) {
            delete userCollapsed[nsk];
          }
        }
      });
    }
  }

  /* Make a just-expanded group's rows visible immediately. Rows hidden as
   * zero-count in the OLD filter context (e.g. Tops' sub-categories while
   * Bottoms was the only category selected) would otherwise only appear
   * ~1.5 s later, after the AJAX round trip. Values S&D does not list at all
   * (no checkbox) stay hidden. */
  function revealChildren(sib) {
    sib.querySelectorAll(':scope > .sc-tree__node').forEach(function (n) {
      if (n.classList.contains('sc-tree__node--disabled')) return;
      n.classList.remove('sc-tree__node--zero', 'sc-tree__node--dim');
    });
  }

  function handleClick(e) {
    var node = e.target.closest('[data-filter-key]');
    var isDisabled = node && node.classList.contains('sc-tree__node--disabled');
    if (!node || (isDisabled && node.dataset.level !== '2') || !container.contains(node)) return;

    var key = node.dataset.filterKey, value = node.dataset.filterValue;
    var pg = node.dataset.parentGender || null;

    // Rebuild lookup for fresh checkbox references
    lookup = buildCheckboxLookup();
    var info = lookup[key] && lookup[key][value];
    if (!info) return;

    var sk = skey(key, value, pg);
    var wantActive = !isDesired(key, value, pg); // toggle
    var sib = node.nextElementSibling;
    var hasSib = sib && sib.classList.contains('sc-tree__children');

    if (!wantActive) {
      // --- Clicking an already-active node ---

      // Parent nodes (L1 + L2): toggle expand/collapse only — never deselect.
      // Deselection happens via gender mutual exclusion, facet pills, or Clear All.
      // Sibling L2 categories going to 0-count when a peer is selected is normal
      // Shopify S&D behavior (disabled checkboxes), not a tree bug.
      if (hasSib) {
        var isExpanded = node.classList.contains('sc-tree__node--expanded');
        node.classList.toggle('sc-tree__node--expanded', !isExpanded);
        sib.classList.toggle('sc-tree__children--visible', !isExpanded);
        // Track intent so syncTreeState doesn't force re-expand after AJAX
        if (isExpanded) {
          userCollapsed[sk] = true;
        } else {
          delete userCollapsed[sk];
        }
        // Flush any pending selections (desired but not yet confirmed in URL)
        // so rapid click-then-collapse doesn't lose the last selection
        var hasPending = Object.keys(desired).some(function (k) {
          return desired[k] && !confirmed[k];
        });
        if (hasPending) {
          stampCheckboxes();
          info.el.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return; // no filter change from collapse itself
      }

      // L2/L3: deselect normally + cascade children
      desired[sk] = false;
      delete confirmed[sk];
      node.classList.remove('sc-tree__node--active');

      // Cascade deselection to children
      if (hasSib) {
        sib.querySelectorAll('[data-filter-key]').forEach(function (ch) {
          var ck = ch.dataset.filterKey, cv = ch.dataset.filterValue;
          var cpg = ch.dataset.parentGender || null;
          var csk = skey(ck, cv, cpg);
          desired[csk] = false;
          delete confirmed[csk];
          ch.classList.remove('sc-tree__node--active', 'sc-tree__node--expanded');
        });
        sib.querySelectorAll('.sc-tree__children--visible').forEach(function (c) { c.classList.remove('sc-tree__children--visible'); });
        node.classList.remove('sc-tree__node--expanded');
        sib.classList.remove('sc-tree__children--visible');
      }
    } else {
      // --- Selecting ---

      // Gender mutual exclusion: selecting one gender deselects others + their children.
      // Prevents cross-product interference (e.g., Women's sub_categories zeroing Men's counts).
      if (key === 'target_gender') {
        container.querySelectorAll('.sc-tree__node[data-filter-key="target_gender"]').forEach(function (gn) {
          if (gn === node) return; // skip the one being selected
          var gv = gn.dataset.filterValue;
          var gsk = skey('target_gender', gv, null);
          if (!isDesired('target_gender', gv, null)) return; // already off
          desired[gsk] = false;
          delete confirmed[gsk];
          delete userCollapsed[gsk];
          gn.classList.remove('sc-tree__node--active', 'sc-tree__node--expanded');
          // Cascade: deselect all children scoped to this gender
          var gsib = gn.nextElementSibling;
          if (gsib && gsib.classList.contains('sc-tree__children')) {
            gsib.querySelectorAll('[data-filter-key]').forEach(function (ch) {
              var csk = skey(ch.dataset.filterKey, ch.dataset.filterValue, ch.dataset.parentGender || null);
              desired[csk] = false;
              delete confirmed[csk];
              delete userCollapsed[csk];
              ch.classList.remove('sc-tree__node--active', 'sc-tree__node--expanded');
            });
            gsib.querySelectorAll('.sc-tree__children--visible').forEach(function (c) { c.classList.remove('sc-tree__children--visible'); });
            gsib.classList.remove('sc-tree__children--visible');
          }
        });
      }

      // When selecting L3, auto-select its parent L2 from the hierarchy JSON
      // so both main_category values use OR and the cross-group AND doesn't conflict.
      if (key === 'sub_category' && pg) {
        var gCats = hierarchy[pg];
        if (gCats) {
          for (var cat in gCats) {
            if (Array.isArray(gCats[cat]) && gCats[cat].indexOf(value) !== -1) {
              desired[skey('main_category', cat, pg)] = true;
              break;
            }
          }
        }
      }

      desired[sk] = true;
      delete userCollapsed[sk];
      node.classList.add('sc-tree__node--active');

      if (hasSib) {
        node.classList.add('sc-tree__node--expanded');
        sib.classList.add('sc-tree__children--visible');
        // Show the child rows now, from the hierarchy the page already has.
        // Their counts are stale until the AJAX response, so hide the numbers
        // (never the rows) until syncTreeState fills them in.
        revealChildren(sib);
        sib.classList.add('sc-tree__counts-pending');
      }
    }

    // Stamp ALL checkboxes (OR across genders for shared values)
    stampCheckboxes();

    // Dispatch ONE change event to trigger Shopify's debounced AJAX
    info.el.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function initCategoryTree() {
    container = document.getElementById('sc-category-tree');
    if (!container) return;
    var script = container.querySelector('script[data-tree-hierarchy]');
    if (!script) return;
    try { hierarchy = JSON.parse(script.textContent); }
    catch (err) { console.error('Category tree: invalid JSON', err); return; }

    lookup = buildCheckboxLookup();
    var hasAny = KEYS.some(function (k) { return Object.keys(lookup[k]).length > 0; });
    if (!hasAny) { container.style.display = 'none'; return; }

    initDesiredFromCheckboxes(lookup);

    var content = document.createElement('div');
    content.className = 'sc-tree__content';
    content.innerHTML = renderTree(hierarchy, lookup);
    container.appendChild(content);

    document.addEventListener('click', handleClick);

    var wrapper = document.getElementById('FacetsWrapperMobile');
    if (wrapper) {
      var obs = new MutationObserver(function (records) {
        // Only react to the filter lists being re-rendered by facets.js. The
        // tree's own count updates are childList mutations too (textContent
        // swaps text nodes); without this guard every sync scheduled the next
        // one, 80 ms apart, for as long as the page stayed open.
        var external = records.some(function (r) { return !container.contains(r.target); });
        if (!external) return;
        clearTimeout(obs._t); obs._t = setTimeout(syncTreeState, 80);
      });
      obs.observe(wrapper, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initCategoryTree);
  else initCategoryTree();
})();
